import React, { useEffect, useRef, useCallback } from 'react'
import get from 'lodash/get'
import PropTypes from 'prop-types'
import cx from 'classnames'
import { toast } from 'react-toastify'
import Progress from '../../Progress/Progress'
import ErrorComponent from '../../Error/Error'
import { useInquireLoyalty } from '../use-loyalty'
import { toastEachWarning } from '../../Modifiers/ToastNotification/ToastNotification'
import { useLoyaltyRedemption } from './use-loyalty-redemption'
import Loading from '../../Loading/Loading'
import Dollars from '../../Dollars/Dollars'
import Fieldset from '../../Modifiers/Fieldset/Fieldset'

import Star from '../../../assets/icon-rewards-star.svg'

import './LoyaltyRedemption.css'

const onCartOutOfStock = (message) => {
  toastEachWarning({ warnings: [message] })
}

export const LoyaltyRedemption = ({ cart }) => {
  const [
    inquireLoyalty,
    loyaltyInquiry
  ] = useInquireLoyalty({ onCartOutOfStock })

  useEffect(function inquireLoyaltyOnMount () {
    inquireLoyalty()
  }, [inquireLoyalty])

  const getInLoyaltyInquiry = useCallback(
    /**
     * @param {string[]|string} path
     * @param {*} [defaultValue]
     * @returns {*} - resolved return value from path
     */
    (path, defaultValue) => {
      const pathArg = Array.isArray(path)
        ? ['data', 'loyaltyInquiry'].concat(path)
        : `data.loyaltyInquiry.${path}`
      return get(loyaltyInquiry, pathArg, defaultValue)
    },
    [loyaltyInquiry]
  )

  /** Expect that first element in the redemptions array is our 'primary' rewards redemption */
  const redemption = getInLoyaltyInquiry(['redemptions', 0], null)

  const {
    addError,
    removeError,
    addRedemption,
    removeRedemption,
    redemptionGuidOnCart,
    addLoading,
    removeLoading,
    remainingBalance
  } = useLoyaltyRedemption({ cart, redemption })

  /** undefined means redemptionGuid not let loaded,
   * null means loaded and no loyalty redemption is available */
  const prevRedemptionGuidOnCart = useRef(undefined)

  useEffect(function trackPrevRedemptionGuidWhenLoaded () {
    if (loyaltyInquiry.data && prevRedemptionGuidOnCart.current === undefined) {
      prevRedemptionGuidOnCart.current = redemptionGuidOnCart
    }
  }, [loyaltyInquiry.data, redemptionGuidOnCart])

  useEffect(function toastWhenRedemptionOnCartChanges () {
    if (prevRedemptionGuidOnCart.current === undefined) return

    if (prevRedemptionGuidOnCart.current !== redemptionGuidOnCart) {
      const message = redemptionGuidOnCart ? 'Reward has been applied!' : 'Reward will be saved for later.'
      toast(message)
      prevRedemptionGuidOnCart.current = redemptionGuidOnCart
    }
  }, [loyaltyInquiry.data, redemptionGuidOnCart])

  if (loyaltyInquiry.loading) {
    return <Progress eventData={{ category: 'LoyaltyInquiry' }} />
  }

  if (!loyaltyInquiry.called || loyaltyInquiry.error || !(getInLoyaltyInquiry('loyaltyAccountIdentifier', null))) {
    return null
  }

  if (!redemption) {
    return (
      <Fieldset label='Rewards' collapsable>
        <span>No available rewards to redeem</span>
      </Fieldset>
    )
  }

  const anyLoading = addLoading || removeLoading
  /** if removing a redemption, then inactive. Otherwise, active when loading or currently selected redemption */
  const isRedeemedSelected = redemptionGuidOnCart === redemption.redemptionGuid
  const redeemedIsActive = !removeLoading && (isRedeemedSelected || addLoading)

  return (
    <Fieldset label='Rewards' collapsable>
      <div styleName='rewardText' data-testid='reward-text'>
        <Star />
          Nice! You have a{' '}
        <span styleName='rewardValue'>
          <Dollars
            amount={redemption.redemptionAmount}
            truncateZeroCents
          />{' '}
            reward
        </span>
      </div>
      {addError && <ErrorComponent error={addError} />}
      {removeError && <ErrorComponent error={removeError} />}
      <div styleName='rewardButtonGroup'>
        <button
          type='button'
          styleName={cx('rewardButton', {
            /** If a selectedGuid is present, only active when remove loading, otherwise only active if not loading an addRedemption  */
            active: redemptionGuidOnCart ? removeLoading : !addLoading
          })}
          data-testid='save-redemption-for-later'
          /** Disabled if any loading state, or selectedGuid is not present */
          disabled={anyLoading || !redemptionGuidOnCart}
          onClick={removeRedemption}
        >
          {removeLoading ? (
            <Loading style='secondary' centerDots />
          ) : (
            'Save for later'
          )}
        </button>
        <button
          type='button'
          styleName={cx('rewardButton', { active: redeemedIsActive })}
          data-testid='use-redemption-0'
          /** Disabled if any loading state, or selectedGuid matches this redemption */
          disabled={anyLoading || redeemedIsActive}
          onClick={addRedemption}
        >
          {addLoading ? (
            <Loading style='secondary' centerDots />
          ) : (
            'Redeem now'
          )}
        </button>
      </div>
      {Boolean(remainingBalance) && (
        <div
          styleName='remainingBalanceText'
          data-testid='remaining-balance-text'
        >
            You have a{' '}
          <Dollars amount={remainingBalance} truncateZeroCents />{' '}
            reward remaining for next time.
        </div>
      )}
    </Fieldset>
  )
}

LoyaltyRedemption.propTypes = {
  cart: PropTypes.object.isRequired
}
